import React, { useState } from 'react';

function UnitListPage({
  ownerUnits = [],
  unitTenantMap = {},
  setActiveTab,
  setShowAddUnitForm,
  allowAddUnit = true,
}) {
  const [filter, setFilter] = useState('');
  const [sortBy, setSortBy] = useState('unitNumber');
  const [sortDir, setSortDir] = useState('asc');

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr + 'T00:00:00'); // avoid timezone shift
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return 'N/A';
    }
  };

  const getTenantCount = (unit) =>
    (unitTenantMap[String(unit.unitNumber)] || []).length;

  const filteredUnits = ownerUnits.filter((unit) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      (unit.unitNumber || '').toString().toLowerCase().includes(q) ||
      (unit.address || unit.formattedAddress || '').toLowerCase().includes(q) ||
      (unit.city || '').toLowerCase().includes(q)
    );
  });

  function handleSort(col) {
    if (sortBy === col) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setSortDir('asc');
    }
  }

  const sortedUnits = [...filteredUnits].sort((a, b) => {
    let av, bv;
    if (sortBy === 'tenants') {
      av = getTenantCount(a);
      bv = getTenantCount(b);
    } else {
      av = a[sortBy] ?? '';
      bv = b[sortBy] ?? '';
    }
    if (typeof av === 'number' || typeof bv === 'number') {
      return sortDir === 'asc' ? av - bv : bv - av;
    }
    const as = String(av).toLowerCase();
    const bs = String(bv).toLowerCase();
    if (as < bs) return sortDir === 'asc' ? -1 : 1;
    if (as > bs) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  return (
    <div className="tenants-container">
      <div className="tenants-header tenants-header--unit-list">
        <div>
          <h1>All units</h1>
          <p className="muted">{ownerUnits.length} unit{ownerUnits.length !== 1 ? 's' : ''}</p>
        </div>
      </div>

      <div className="unit-list-actions">
        <input
          className="tenant-search unit-list-search"
          placeholder="Search unit or address..."
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Search units by number or address"
        />
        {allowAddUnit ? (
          <button
            type="button"
            className="unit-list-add-unit-btn"
            data-owner-tip-target="owner-unit-list-add"
            onClick={() => {
              if (setShowAddUnitForm) setShowAddUnitForm(true);
              if (setActiveTab) setActiveTab('add');
            }}
          >
            ➕ Add new unit
          </button>
        ) : null}
      </div>

      <div className="tenants-sort-bar">
        <span className="sort-label">Sort:</span>
        {[
          { key: 'unitNumber', label: 'Unit' },
          { key: 'monthlyRent', label: 'Rent' },
          { key: 'leaseEnd', label: 'Lease Expiration' },
          { key: 'tenants', label: 'Tenants' },
        ].map(({ key, label }) => (
          <button
            key={key}
            className={`sort-btn${sortBy === key ? ' sort-btn-active' : ''}`}
            onClick={() => handleSort(key)}
          >
            {label}{sortBy === key ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
          </button>
        ))}
      </div>

      {sortedUnits.length > 0 ? (
        <div className="tenants-grid">
          {sortedUnits.map((unit) => {
            const tenantCount = getTenantCount(unit);
            const address = unit.formattedAddress || unit.address || '';
            const today = new Date();
            const leaseEnd = unit.leaseEnd ? new Date(unit.leaseEnd + 'T00:00:00') : null;
            const leaseExpired = leaseEnd ? leaseEnd < today : false;
            return (
              <div
                key={unit.id}
                className="tenant-card"
                style={{ cursor: 'pointer' }}
                onClick={() => setActiveTab && setActiveTab(unit.id)}
              >
                {/* Card header */}
                <div className="tenant-card-header">
                  <span className="tenant-avatar-lg" style={{ background: '#eef2ff', color: '#1e3a8a', fontSize: '13px' }}>
                    {unit.unitNumber ? String(unit.unitNumber) : 'U'}
                  </span>
                  <div className="tenant-card-identity">
                    <div className="tenant-fullname">Unit {unit.unitNumber || 'N/A'}</div>
                    <div className="tenant-sub">{address || 'No address set'}</div>
                  </div>
                  {tenantCount > 0 ? (
                    <span className="tenant-badge badge-open">{tenantCount} tenant{tenantCount !== 1 ? 's' : ''}</span>
                  ) : leaseExpired ? (
                    <span className="tenant-badge badge-expired">Lease expired</span>
                  ) : (
                    <span className="tenant-badge badge-none">Vacant</span>
                  )}
                </div>

                {/* Card body */}
                <div className="tenant-card-body">
                  <div className="tenant-stat">
                    <span className="tenant-stat-label">Monthly Rent</span>
                    <span className="tenant-stat-value tenant-money">{formatCurrency(unit.monthlyRent)}</span>
                  </div>
                  <div className="tenant-stat">
                    <span className="tenant-stat-label">Lease Start</span>
                    <span className="tenant-stat-value">{formatDate(unit.leaseStart)}</span>
                  </div>
                  <div className="tenant-stat tenant-stat-last">
                    <span className="tenant-stat-label">Lease Expiration</span>
                    <span className="tenant-stat-value">{formatDate(unit.leaseEnd)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="empty-state">
          {ownerUnits.length === 0 ? (
            <div style={{ textAlign: 'center', maxWidth: 420, margin: '0 auto' }}>
              <p style={{ margin: '0 0 16px', fontSize: 16, color: 'var(--muted, #6b7280)' }}>
                No units yet. Add your first rental unit to get started.
              </p>
              {allowAddUnit ? (
                <button
                  type="button"
                  className="primary-btn"
                  onClick={() => {
                    if (setShowAddUnitForm) setShowAddUnitForm(true);
                    if (setActiveTab) setActiveTab('add');
                  }}
                  style={{
                    padding: '12px 22px',
                    borderRadius: 10,
                    border: 'none',
                    background: '#2563eb',
                    color: '#fff',
                    cursor: 'pointer',
                    fontWeight: 700,
                    fontSize: 15,
                  }}
                >
                  ➕ Add your first unit
                </button>
              ) : null}
            </div>
          ) : (
            'No units match your search.'
          )}
        </div>
      )}
    </div>
  );
}

export default UnitListPage;
